/**
 * PricingCompareTable — iter87.199.75
 *
 * Free vs Pro+ side-by-side comparison table. Every row's data-testid
 * is `pricing-row-<slug>` — those slugs are grep-locked against
 * `memory/pricing_registry.md` by
 * `test_iter87_199_75_pro_plus_landing.py::test_every_registry_row_appears_in_table`.
 *
 * If you add / rename a row here, update the registry AND the test
 * in the same commit.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import ProPlusVideoModalLink from "./ProPlusVideoModalLink";

const DASH = <span className="text-zinc-700 text-lg">—</span>;
const YES  = <Check className="w-4 h-4 text-sky-400 inline" />;

const ROWS = [
    { slug: "rivalries",              label: "Rivalries",                                free: "1",         plus: "As many as you'd like" },
    { slug: "tournaments-per-rivalry", label: "Tournaments per rivalry",                 free: "1",         plus: "5" },
    { slug: "scoring-engines",        label: "Every sport & scoring engine",             free: YES,         plus: YES },
    { slug: "matchday-boost",         label: "Weekly Spread Boost & Matchday Boost",     free: YES,         plus: YES },
    { slug: "ai-friend-previews",     label: "AI Friend — weekly matchup previews",      free: DASH,        plus: YES },
    { slug: "ai-friend-recap",        label: "AI Friend — full season story-writer",     free: DASH,        plus: YES },
    { slug: "trophy-tokens",          label: "Trophy Tokens — track who's in and out",   free: DASH,        plus: YES },
    { slug: "multi-crew-hub",         label: "Multi-crew hub — every rivalry in one place", free: DASH,     plus: YES },
    { slug: "todo-reminder-emails",   label: "Email reminders for any to-dos",           free: DASH,        plus: YES },
    { slug: "never-archived",         label: "Rivalries live forever (as long as you're a Pro+ subscriber)",  free: DASH,        plus: YES },
    { slug: "cancel-anytime",         label: "Cancel anytime",                            free: YES,         plus: YES },
];

export default function PricingCompareTable() {
    return (
        <section
            className="max-w-4xl mx-auto px-4 py-16"
            aria-label="Compare Free and Pro+ plans"
            data-testid="pro-plus-pricing-compare"
        >
            <div className="mb-8 text-center">
                <p className="text-[10px] uppercase tracking-[0.3em] text-sky-400 font-black mb-2">
                    Pricing
                </p>
                <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
                    Simple. Five bucks.
                </h2>
                <p className="text-sm text-zinc-400 mt-3 max-w-md mx-auto">
                    One tier. One price. All the Pro+ features. Cancel any time — your rivalries stay yours.
                </p>
                <div className="mt-3 flex justify-center">
                    <ProPlusVideoModalLink />
                </div>
            </div>

            <div className="grid grid-cols-3 gap-0 border border-zinc-800 rounded-lg overflow-hidden">
                {/* Header row */}
                <div className="px-4 py-4 bg-zinc-900/60 border-b border-r border-zinc-800">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Feature</p>
                </div>
                <div className="px-4 py-4 bg-zinc-900/60 border-b border-r border-zinc-800 text-center">
                    <p className="text-sm font-black text-white">Free</p>
                    <p className="text-[10px] text-zinc-500 mt-1">$0</p>
                </div>
                <div className="px-4 py-4 bg-sky-400/[0.06] border-b border-sky-400/40 text-center">
                    <p className="text-sm font-black text-sky-400">Pro+</p>
                    <p className="text-[10px] text-zinc-400 mt-1" data-testid="pro-plus-pricing-headline-price">$5 / month</p>
                </div>

                {/* Rows */}
                {ROWS.map((r) => (
                    <React.Fragment key={r.slug}>
                        <div
                            className="px-4 py-3 border-b border-r border-zinc-800 text-xs text-zinc-300"
                            data-testid={`pricing-row-${r.slug}`}
                        >
                            {r.label}
                        </div>
                        <div className="px-4 py-3 border-b border-r border-zinc-800 text-center text-xs text-zinc-400">
                            {r.free}
                        </div>
                        <div className="px-4 py-3 border-b border-sky-400/20 text-center text-xs text-white bg-sky-400/[0.03]">
                            {r.plus}
                        </div>
                    </React.Fragment>
                ))}

                {/* CTA row */}
                <div className="col-span-3 px-4 py-5 bg-zinc-900/60 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className="text-xs text-zinc-500" data-testid="pricing-compare-footnote">Both plans support up to 20 crewmates per rivalry.</p>
                    <Link
                        to="/rivalry-pro/sign-up"
                        data-testid="pricing-compare-primary-cta"
                        className="brutal-btn bg-sky-400 text-black hover:bg-sky-300 border-transparent text-xs px-5 py-2.5 flex items-center gap-2"
                    >
                        Start Pro+ · $5 / month <ArrowRight className="w-3 h-3" />
                    </Link>
                </div>
            </div>
        </section>
    );
}
